import type { useAppDispatch } from '../grid/reduxStore/Store';
import type { ErrorCode } from '../../../services/socket/SocketEvents';
import { AcknowledgmentResponseStatus } from '../../../services/socket/SocketEvents';
import { addSection, deleteSection } from '../grid/reduxStore/editorSlice';
import { setSaveStatus, setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus';
import type { UndoRedoCommandInterface } from './undoRedoCommandVersion';
import { addSectionViaSocket, deleteSectionViaSocket } from '../grid/reduxStore/saveHandlers';
import { v4 as uuidv4 } from 'uuid';

export interface AddSectionCommandDependencies {
  dispatch: ReturnType<typeof useAppDispatch>;
  socketClient: any;
  checkDocumentLockStatus: (errorCode: ErrorCode) => boolean;
}

export interface AddSectionCommandParams {
  title: string;
  afterSectionId: string;
}

export class AddSectionCommand implements UndoRedoCommandInterface {
  readonly scope = 'section' as const;
  readonly timestamp: string;

  private readonly dispatch: ReturnType<typeof useAppDispatch>;
  private readonly socketClient: any;
  private readonly checkDocumentLockStatus: (errorCode: ErrorCode) => boolean;

  private readonly title: string;
  private readonly afterSectionId: string;
  private currentSectionId: string;

  constructor(dependencies: AddSectionCommandDependencies, params: AddSectionCommandParams) {
    this.timestamp = new Date().toISOString();
    this.dispatch = dependencies.dispatch;
    this.socketClient = dependencies.socketClient;
    this.checkDocumentLockStatus = dependencies.checkDocumentLockStatus;
    this.title = params.title;
    this.afterSectionId = params.afterSectionId;
    this.currentSectionId = uuidv4();
  }

  async execute(): Promise<string | null> {
    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const response = await addSectionViaSocket(this.socketClient, {
        id: this.currentSectionId,
        title: this.title,
        afterSectionId: this.afterSectionId,
      });

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return null;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return null;
      }

      this.dispatch(
        addSection({
          sectionId: this.currentSectionId,
          title: this.title,
          afterSectionId: this.afterSectionId,
        })
      );

      this.dispatch(setSaveStatusBasedOnApiResponse({ status: response.status, errorCode: response.errorCode }));

      return this.currentSectionId;
    } catch (error) {
      console.error('AddSectionCommand: execute failed', error);
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return null;
    }
  }

  async undo(): Promise<boolean> {
    if (!this.currentSectionId) {
      console.warn('AddSectionCommand: cannot undo, no sectionId');
      return false;
    }

    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const response = await deleteSectionViaSocket(this.socketClient, { sectionId: this.currentSectionId });

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      this.dispatch(deleteSection({ sectionId: this.currentSectionId }));

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          errorCode: response.errorCode,
        })
      );

      return true;
    } catch (error) {
      console.error('AddSectionCommand: undo failed', error);
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return false;
    }
  }

  async redo(): Promise<boolean> {
    try {
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const response = await addSectionViaSocket(this.socketClient, {
        id: this.currentSectionId,
        title: this.title,
        afterSectionId: this.afterSectionId,
      });

      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
        return false;
      }

      this.dispatch(
        addSection({
          sectionId: this.currentSectionId,
          title: this.title,
          afterSectionId: this.afterSectionId,
        })
      );

      this.dispatch(
        setSaveStatusBasedOnApiResponse({
          status: response.status,
          errorCode: response.errorCode,
        })
      );

      return true;
    } catch (error) {
      console.error('AddSectionCommand: redo failed', error);
      this.dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED }));
      return false;
    }
  }

  getCurrentSectionId(): string {
    return this.currentSectionId;
  }

  toJSON(): Record<string, any> {
    return {
      title: this.title,
      afterSectionId: this.afterSectionId,
      currentSectionId: this.currentSectionId,
    };
  }
}
